import { useEffect, useRef } from 'react';
import { NowPlaying } from '../native/nowPlaying';
import { isNativeApp } from '../utils/platform';

export interface NowPlayingControls {
  play: () => void;
  pause: () => void;
  skipForward: () => void;
  skipBackward: () => void;
  nextTrack: () => void;
  previousTrack: () => void;
  seek: (position: number) => void;
}

interface UseNowPlayingArgs {
  /** True while a book is loaded and playable (mirrors the docked-player mount). */
  enabled: boolean;
  title: string;
  artist?: string;
  album?: string;
  artworkUrl?: string;
  /** Current chapter duration, seconds. */
  duration: number;
  /** Elapsed time within the chapter, seconds. */
  position: number;
  isPlaying: boolean;
  /** Current playback speed, so the OS extrapolates the scrubber correctly. */
  playbackRate: number;
  controls: NowPlayingControls;
}

// Don't push a position update on every frame; the OS extrapolates the scrubber
// from the playback rate. This only corrects drift and reflects seeks.
const POSITION_PUSH_INTERVAL_MS = 3000;

const MEDIA_SESSION_ACTIONS: MediaSessionAction[] = [
  'play',
  'pause',
  'seekbackward',
  'seekforward',
  'previoustrack',
  'nexttrack',
  'seekto',
];

/**
 * Mirrors the audiobook player to the iOS lock screen / Control Center and keeps
 * WKWebView audio alive in the background.
 *
 * Metadata and controls go through the Web MediaSession API — WKWebView runs its
 * own media session for the playing <audio> and reads from navigator.mediaSession
 * (setting it here replaces the document-<title> default and lets us own the
 * lock-screen buttons). The native plugin only forces the AVAudioSession into
 * .playback for background continuation. Entirely gated on isNativeApp(); web is
 * left on its existing behaviour.
 */
export const useNowPlaying = ({
  enabled,
  title,
  artist,
  album,
  artworkUrl,
  duration,
  position,
  isPlaying,
  playbackRate,
  controls,
}: UseNowPlayingArgs): void => {
  // Keep the latest controls/position without re-binding the action handlers.
  const controlsRef = useRef(controls);
  controlsRef.current = controls;
  const positionRef = useRef(position);
  positionRef.current = position;
  const lastPositionPushRef = useRef(0);

  const active = isNativeApp() && enabled;

  // Native audio session: playback category, tied to whether a book is active.
  useEffect(() => {
    if (!active) return;
    void NowPlaying.enable();
    return () => {
      void NowPlaying.disable();
    };
  }, [active]);

  // Remote-control handlers → existing player controls. WebKit routes lock-screen
  // and Control Center presses here; the ref keeps them current without rebinding.
  useEffect(() => {
    if (!active || !('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    ms.setActionHandler('play', () => controlsRef.current.play());
    ms.setActionHandler('pause', () => controlsRef.current.pause());
    ms.setActionHandler('seekbackward', () => controlsRef.current.skipBackward());
    ms.setActionHandler('seekforward', () => controlsRef.current.skipForward());
    ms.setActionHandler('previoustrack', () => controlsRef.current.previousTrack());
    ms.setActionHandler('nexttrack', () => controlsRef.current.nextTrack());
    ms.setActionHandler('seekto', (details) => {
      if (typeof details.seekTime === 'number') controlsRef.current.seek(details.seekTime);
    });
    return () => {
      for (const action of MEDIA_SESSION_ACTIONS) {
        try {
          ms.setActionHandler(action, null);
        } catch {
          /* action unsupported by this engine — nothing to clear */
        }
      }
    };
  }, [active]);

  // Metadata (cover, chapter title, author) — refreshed on book/chapter change.
  useEffect(() => {
    if (!active || !('mediaSession' in navigator) || !title) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist: artist ?? '',
      album: album ?? '',
      artwork: artworkUrl
        ? [{ src: artworkUrl, sizes: '512x512', type: 'image/jpeg' }]
        : [],
    });
  }, [active, title, artist, album, artworkUrl]);

  // Play/pause state.
  useEffect(() => {
    if (!active || !('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [active, isPlaying]);

  // Scrubber position, throttled — continuous playback is extrapolated by the OS.
  useEffect(() => {
    if (!active || !('mediaSession' in navigator) || typeof navigator.mediaSession.setPositionState !== 'function') {
      return;
    }
    const now = Date.now();
    if (now - lastPositionPushRef.current < POSITION_PUSH_INTERVAL_MS) return;
    if (!(duration > 0) || position < 0 || position > duration) return;
    lastPositionPushRef.current = now;
    try {
      navigator.mediaSession.setPositionState({
        duration,
        position,
        playbackRate: isPlaying ? playbackRate : 0,
      });
    } catch {
      /* invalid position/duration transient — skip this update */
    }
  }, [active, position, duration, isPlaying, playbackRate]);
};
